import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Tooltip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { debounce } from 'lodash';
import { SectionFieldCollection, SectionFieldDTO, FieldEntryDTO, FieldType, MagicReportState } from '../magic-report.type';
import { useMagicReport } from '../context/MagicReportContext';
import { normalizeFieldTypes } from './section-utils';
import { CompleteSectionConfig } from './sections.config';
import { getSectionRank } from './action-items.util';
import { BookmarkToggle } from './BookmarkToggle';
import { ActionItemsReorderModal } from './ActionItemsReorderModal';
import { SectionCompleteToggle } from '../../shared/components/SectionCompleteToggle';
import { useCompletionToggleEffect } from '../../../../hooks/useCompletionToggleEffect';

/**
 * Customization options for rendering and behavior
 */
export interface SectionCustomization {
  /** Custom header content (right side of accordion header) */
  renderHeader?: (data: SectionFieldCollection) => React.ReactNode;

  /** Custom content rendering (full control over accordion body) */
  renderContent?: (
    data: SectionFieldCollection,
    handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void,
    getField: (fieldName: string) => SectionFieldDTO | undefined,
    onBook: () => number,
  ) => React.ReactNode;

  /** Custom data transformation function applied after loading from state */
  onDataTransform?: (data: SectionFieldCollection) => SectionFieldCollection;

  /** Function to determine initial expanded state based on section data */
  getInitialExpandedState?: (data: SectionFieldCollection) => boolean;

  /**
   * Optional gate called before marking the section complete (not called when unlocking).
   * Return false (or a Promise resolving to false) to cancel the completion.
   */
  onBeforeComplete?: (data: SectionFieldCollection) => boolean | Promise<boolean>;
}

/**
 * Accordion expansion control options
 */
export interface ExpansionControl {
  /** Controlled expansion state (optional) */
  controlled?: boolean;

  /** Callback for expansion changes */
  onChange?: (expanded: boolean) => void;
}

/**
 * Props for MagicReportSection component
 * Grouped logically to reduce prop explosion
 */
interface MagicReportSectionProps {
  /** Magic Report ID */
  magicReportId: string;

  /** Complete section configuration (id, title, config, layout, etc.) */
  sectionConfig: CompleteSectionConfig;

  /** Optional customizations for rendering and behavior */
  customization?: SectionCustomization;

  /** Optional accordion expansion control */
  expansionControl?: ExpansionControl;
}

/**
 * MagicReportSection - Universal section wrapper for Magic Report sections
 *
 * This component handles all common section logic:
 * - State management and synchronization with global report state
 * - Field change handlers
 * - Data normalization and transformation
 * - Accordion wrapper with customizable header and content
 *
 * All sections must provide custom rendering via DeclarativeLayoutRenderer.
 * Clean API: only 4 props (magicReportId, sectionConfig, customization, expansionControl)
 */
export const MagicReportSection: React.FC<MagicReportSectionProps> = ({
  magicReportId,
  sectionConfig,
  customization,
  expansionControl,
}) => {
  const {
    setIsDirty,
    setReportState,
    reportState,
    registerSectionFlush,
    unregisterSectionFlush,
    claimActionItemOrder,
    registerSectionOrderSync,
    unregisterSectionOrderSync,
    saveReport,
  } = useMagicReport() as any;
  const [localData, setLocalData] = useState<SectionFieldCollection | null>(null);
  const [internalExpanded, setInternalExpanded] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const hasInitializedGlobalState = React.useRef(false);
  const hasInitializedLocalData = React.useRef(false);
  // Tracks the insertion-sequence value claimed when this section was first booked.
  // Cleared when the section is fully unbookmarked so it can claim a fresh slot next time.
  const sectionActionItemOrderRef = React.useRef<number | undefined>(undefined);

  // Extract values from sectionConfig
  const {
    id: sectionId,
    title,
    config,
    getDefaultData,
    fieldOptions,
    accordionSx,
    titleVariant = 'h5',
    titleColor = 'primary',
    hideActionItemBookmark = false,
  } = sectionConfig;

  // Extract customization options
  const { renderHeader, renderContent, onDataTransform, getInitialExpandedState, onBeforeComplete } = customization || {};

  // Extract expansion control options
  const { controlled: controlledExpanded, onChange: onExpandedChange } = expansionControl || {};

  // Use controlled expansion if provided, otherwise use internal state
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  // Live rank derived from global state — recomputes whenever any section is bookmarked/unbookmarked
  const sectionRank = useMemo(() => getSectionRank(reportState.sections, sectionId), [reportState.sections, sectionId]);

  // Initialize section data from reportState or use defaults (run only once on mount)
  useEffect(() => {
    // Only initialize once - don't re-sync from reportState after initial load
    // This prevents reportState updates from overwriting local user changes
    if (hasInitializedLocalData.current) {
      return;
    }

    let sectionData = (reportState.sections as any)?.[sectionId] || getDefaultData(reportState.projectSummary);

    // Normalize field types for backward compatibility
    sectionData = normalizeFieldTypes(sectionData);

    // Apply custom transformation if provided
    if (onDataTransform) {
      sectionData = onDataTransform(sectionData);
    }
    setLocalData(sectionData);

    // Restore the insertion-sequence value from persisted field data (including entries)
    const persistedOrder = sectionData.fields.reduce(
      (min: number | undefined, f: SectionFieldDTO) => {
        let cur = min;
        if (typeof f.actionItemOrder === 'number') {
          cur = cur === undefined ? f.actionItemOrder : Math.min(cur, f.actionItemOrder);
        }
        for (const e of f.entries || []) {
          if (typeof e.actionItemOrder === 'number') {
            cur = cur === undefined ? e.actionItemOrder : Math.min(cur, e.actionItemOrder);
          }
        }
        return cur;
      },
      undefined as number | undefined,
    );
    if (persistedOrder !== undefined) {
      sectionActionItemOrderRef.current = persistedOrder;
    }

    hasInitializedLocalData.current = true;

    // If section doesn't exist in global state, initialize it (only once to avoid infinite loop)
    if (!(reportState.sections as any)?.[sectionId] && !hasInitializedGlobalState.current) {
      hasInitializedGlobalState.current = true;
      setReportState((prev: MagicReportState) => ({
        ...prev,
        sections: { ...(prev.sections || {}), [sectionId]: sectionData } as any,
      }));
    }

    // Set initial expanded state if function provided and not yet initialized
    if (!hasInitialized && controlledExpanded === undefined) {
      if (sectionData.isCompleted) {
        // Completed sections always start collapsed, overriding any custom initial-expand logic.
        setInternalExpanded(false);
      } else if (getInitialExpandedState) {
        setInternalExpanded(getInitialExpandedState(sectionData));
      }
      setHasInitialized(true);
    }
  }, []); // Empty deps - only run once on mount to prevent overwriting local changes

  /**
   * Returns the insertion-sequence number for this section.
   * First call claims the next available slot; subsequent calls return the cached value.
   * The ref is cleared when the section is fully unbookmarked so it can claim a new slot
   * if re-bookmarked later (taking the next available position in the queue at that point).
   */
  const onBook = useCallback((): number => {
    if (sectionActionItemOrderRef.current !== undefined) {
      return sectionActionItemOrderRef.current;
    }
    const order = claimActionItemOrder();
    sectionActionItemOrderRef.current = order;
    return order;
  }, [claimActionItemOrder]);

  // Debounced global state update (500ms delay)
  const debouncedGlobalStateUpdate = useMemo(
    () =>
      debounce((fieldName: string, adjustedUpdates: Partial<SectionFieldDTO>, localDataFields: SectionFieldDTO[], projectSummary: any) => {
        // Use flushSync to ensure state updates are processed immediately when flushed
        flushSync(() => {
          setReportState((prev: MagicReportState) => {
            const currentSection = (prev.sections as any)?.[sectionId];

            // Use existing section or fall back to localData fields
            const baseFields = currentSection?.fields || localDataFields || [];
            const fieldExists = baseFields.some((f: any) => f.fieldName === fieldName);
            let updatedFields;

            if (fieldExists) {
              updatedFields = baseFields.map((f: any) => (f.fieldName === fieldName ? { ...f, ...adjustedUpdates } : f));
            } else {
              // Field doesn't exist - add it with updates
              const defaultData = getDefaultData(prev.projectSummary || projectSummary);
              const defaultField = defaultData.fields.find(f => f.fieldName === fieldName);
              const newField: SectionFieldDTO = defaultField
                ? { ...defaultField, ...adjustedUpdates }
                : { fieldName, fieldType: FieldType.Text, ...adjustedUpdates };
              updatedFields = [...baseFields, newField];
            }

            return {
              ...prev,
              sections: { ...(prev.sections || {}), [sectionId]: { ...currentSection, fields: updatedFields } } as any,
            };
          });

          setIsDirty(true);
        });
      }, 500), // 500ms debounce delay
    [sectionId, setReportState, setIsDirty, getDefaultData],
  );

  // Sync all local data to global state (used when saving)
  const syncToGlobalState = useCallback(() => {
    if (!localData) return;

    // Use flushSync to ensure immediate update
    flushSync(() => {
      setReportState((prev: MagicReportState) => ({
        ...prev,
        sections: { ...(prev.sections || {}), [sectionId]: localData } as any,
      }));
      setIsDirty(true);
    });
  }, [localData, sectionId, setReportState, setIsDirty]);

  // Expose flush function for manual save
  const flushPendingUpdates = useCallback(() => {
    // First flush any pending debounced updates
    debouncedGlobalStateUpdate.flush();
    // Then sync the entire local state to ensure everything is captured
    syncToGlobalState();
  }, [debouncedGlobalStateUpdate, syncToGlobalState]);

  // Register flush function with context on mount, unregister on unmount
  useEffect(() => {
    if (registerSectionFlush) {
      registerSectionFlush(sectionId, flushPendingUpdates);
    }
    return () => {
      if (unregisterSectionFlush) {
        unregisterSectionFlush(sectionId);
      }
    };
  }, [sectionId, flushPendingUpdates, registerSectionFlush, unregisterSectionFlush]);

  // Register order-sync callback so reorders patch localData without a full re-init
  useEffect(() => {
    if (!registerSectionOrderSync) return;
    registerSectionOrderSync(sectionId, (updatedSections: any) => {
      const updatedCollection = updatedSections?.[sectionId];
      if (!updatedCollection?.fields) return;
      setLocalData(prev => {
        if (!prev) return prev;
        const updatedFields = prev.fields.map((f: SectionFieldDTO) => {
          const match = updatedCollection.fields.find((uf: SectionFieldDTO) => uf.fieldName === f.fieldName);
          if (!match) return f;
          const updatedEntries = (f.entries || []).map((e: FieldEntryDTO, ei: number) => {
            const me = match.entries?.[ei];
            return me !== undefined ? { ...e, actionItemOrder: me.actionItemOrder } : e;
          });
          return { ...f, actionItemOrder: match.actionItemOrder, entries: updatedEntries.length ? updatedEntries : f.entries };
        });
        return { ...prev, fields: updatedFields };
      });
    });
    return () => {
      if (unregisterSectionOrderSync) unregisterSectionOrderSync(sectionId);
    };
  }, [sectionId, registerSectionOrderSync, unregisterSectionOrderSync]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedGlobalStateUpdate.cancel();
    };
  }, [debouncedGlobalStateUpdate]);

  // Common field change handler with optimistic updates
  const handleFieldChange = useCallback(
    (fieldName: string, updates: Partial<SectionFieldDTO>) => {
      const computeAdjustedUpdates = (currentField?: SectionFieldDTO) => {
        // Only intervene when bookmark is being toggled via this handler
        if (updates.addToActionItems === true) {
          const order = onBook();
          return {
            ...updates,
            actionItemOrder: updates.actionItemOrder ?? currentField?.actionItemOrder ?? order,
          };
        }
        if (updates.addToActionItems === false) {
          return { ...updates, actionItemOrder: undefined };
        }
        return updates;
      };

      // Immediate local state update for responsive UI
      setLocalData(prevData => {
        if (!prevData) return prevData;
        const currentField = prevData.fields.find(f => f.fieldName === fieldName);
        const adjustedUpdates = computeAdjustedUpdates(currentField);

        // If field doesn't exist, add it; otherwise update it
        const fieldExists = prevData.fields.some(f => f.fieldName === fieldName);
        let updatedFields;
        if (fieldExists) {
          updatedFields = prevData.fields.map(f => (f.fieldName === fieldName ? { ...f, ...adjustedUpdates } : f));
        } else {
          // Field doesn't exist - add it with updates
          const defaultData = getDefaultData(reportState.projectSummary);
          const defaultField = defaultData.fields.find(f => f.fieldName === fieldName);
          const newField: SectionFieldDTO = defaultField
            ? { ...defaultField, ...adjustedUpdates }
            : { fieldName, fieldType: FieldType.Text, ...adjustedUpdates };
          updatedFields = [...prevData.fields, newField];
        }

        const newData = { ...prevData, fields: updatedFields };

        // After an unbookmark, check if the section now has zero active bookmarks.
        // If so, clear the insertion-sequence ref so re-booking claims a fresh slot.
        if (updates.addToActionItems === false) {
          const stillHasBookmark = newData.fields.some(
            f => f.addToActionItems === true || (f.entries || []).some(e => e.addToActionItems === true),
          );
          if (!stillHasBookmark) {
            sectionActionItemOrderRef.current = undefined;
          }
        }

        // Debounced global state update (after local state is updated)
        const updatedField = newData.fields.find(f => f.fieldName === fieldName);
        const adjustedUpdatesForGlobal = computeAdjustedUpdates(updatedField);
        debouncedGlobalStateUpdate(fieldName, adjustedUpdatesForGlobal, newData.fields, reportState.projectSummary);

        return newData;
      });
    },
    [onBook, reportState.projectSummary, getDefaultData, debouncedGlobalStateUpdate],
  );

  // Helper to get a specific field. Reads through a ref so its identity stays stable across
  // edits (localData changes on every field update) instead of being recreated each render,
  // which would defeat useMemo/useCallback in consumers that depend on getField.
  const localDataRef = React.useRef(localData);
  localDataRef.current = localData;
  const getField = useCallback((fieldName: string): SectionFieldDTO | undefined => {
    return localDataRef.current?.fields.find(f => f.fieldName === fieldName);
  }, []);

  // Check if all bookmarks in the section are checked
  const allBookmarksChecked = useMemo(() => {
    if (!localData?.fields || localData.fields.length === 0) return false;

    // Collect all bookmarkable items (fields and entries)
    const bookmarkableFields: SectionFieldDTO[] = [];
    const bookmarkableEntries: Array<{ field: SectionFieldDTO; entry: FieldEntryDTO }> = [];

    localData.fields.forEach(f => {
      // Fields that can be bookmarked (skip readonly calculated fields)
      if (f.addToActionItems !== undefined) {
        bookmarkableFields.push(f);
      }
      // Entries in TextArray fields (like Comments)
      if (f.entries && f.entries.length > 0) {
        f.entries.forEach(e => {
          if (e.addToActionItems !== undefined) {
            bookmarkableEntries.push({ field: f, entry: e });
          }
        });
      }
    });

    // If there are no bookmarkable items, return false
    if (bookmarkableFields.length === 0 && bookmarkableEntries.length === 0) return false;

    // Check if all bookmarkable items are checked
    const allFieldsChecked = bookmarkableFields.every(f => f.addToActionItems === true);
    const allEntriesChecked = bookmarkableEntries.every(({ entry }) => entry.addToActionItems === true);

    return allFieldsChecked && allEntriesChecked;
  }, [localData?.fields]);

  // Handler to toggle all bookmarks in the section
  const handleToggleAllBookmarks = useCallback(
    (e?: React.MouseEvent) => {
      // Stop event propagation to prevent accordion from toggling
      if (e) {
        e.stopPropagation();
      }

      if (!localData?.fields) return;

      const newValue = !allBookmarksChecked;

      // When turning off all bookmarks, clear the ref so re-booking gets a fresh queue slot
      if (!newValue) {
        sectionActionItemOrderRef.current = undefined;
      }

      const order = newValue ? onBook() : 0;

      // Update all fields
      const updatedFields = localData.fields.map((f: SectionFieldDTO) => {
        // Skip fields that don't support bookmarks
        if (f.addToActionItems === undefined) return f;

        // Update entries for TextArray fields (like Comments)
        if (f.entries && f.entries.length > 0) {
          const updatedEntries = f.entries.map((e: FieldEntryDTO) => {
            if (e.addToActionItems === undefined) return e;
            return {
              ...e,
              addToActionItems: newValue,
              actionItemOrder: newValue ? (e.actionItemOrder ?? order) : undefined,
            };
          });
          return {
            ...f,
            entries: updatedEntries,
            addToActionItems: newValue,
            actionItemOrder: newValue ? (f.actionItemOrder ?? order) : undefined,
          };
        }

        // Regular field
        return {
          ...f,
          addToActionItems: newValue,
          actionItemOrder: newValue ? (f.actionItemOrder ?? order) : undefined,
        };
      });

      // Update both local and global state
      setLocalData(prev => ({ ...prev, fields: updatedFields }));

      setReportState((prev: MagicReportState) => ({
        ...prev,
        sections: {
          ...(prev.sections || {}),
          [sectionId]: { ...((prev.sections as any)?.[sectionId] || {}), fields: updatedFields },
        } as any,
      }));

      setIsDirty(true);
    },
    [localData?.fields, allBookmarksChecked, onBook, sectionId, setIsDirty, setReportState],
  );

  // only editing is locked, not visibility.
  const handleExpandChange = () => {
    const newExpanded = !expanded;
    if (controlledExpanded === undefined) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };

  // Toggle the "complete" state for this section: persists to global state immediately,
  // collapses the accordion on completion (still re-expandable afterward to view read-only content).
  // Fields become read-only while completed until toggled back to incomplete.
  const handleToggleCompleted = useCallback(async () => {
    if (!localData?.isCompleted && onBeforeComplete) {
      const canComplete = await onBeforeComplete(localData as SectionFieldCollection);
      if (!canComplete) return;
    }
    setLocalData(prev => (prev ? { ...prev, isCompleted: !prev.isCompleted } : prev));
  }, [localData, onBeforeComplete]);

  const { isSavingCompletion } = useCompletionToggleEffect({
    isCompleted: Boolean(localData?.isCompleted),
    enabled: Boolean(localData),
    onToggle: nextCompleted => {
      setReportState((prevState: MagicReportState) => ({
        ...prevState,
        sections: { ...(prevState.sections || {}), [sectionId]: localData } as any,
      }));
      setIsDirty(true);
      if (controlledExpanded === undefined) {
        // Collapse on completion; re-expand automatically on unlock so the user lands
        // straight back into the now-editable fields instead of a collapsed shell.
        setInternalExpanded(!nextCompleted);
      }
    },
    saveReport,
  });

  if (!localData || !renderContent) {
    return null;
  }

  const isCompleted = Boolean(localData.isCompleted);

  const bookmarkWithChip = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {sectionRank !== undefined ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '999px',
            bgcolor: 'grey.200',
            overflow: 'hidden',
          }}
        >
          <Tooltip title='Reorder action items' placement='top'>
            <Box
              onClick={e => {
                e.stopPropagation();
                setReorderModalOpen(true);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                pl: 1.5,
                pr: 1.25,
                py: 0.625,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { bgcolor: 'grey.300' },
                transition: 'background-color 0.15s',
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>{sectionRank}</Typography>
            </Box>
          </Tooltip>
          <Box sx={{ width: '1px', height: 20, bgcolor: 'grey.400', flexShrink: 0 }} />
          <Tooltip title={allBookmarksChecked ? 'Remove all bookmarks' : 'Bookmark all'} placement='top'>
            <Box
              onClick={e => {
                e.stopPropagation();
                handleToggleAllBookmarks(e);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: 1.25,
                pr: 1.5,
                py: 0.625,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.300' },
                transition: 'background-color 0.15s',
              }}
            >
              {allBookmarksChecked ? (
                <BookmarkIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
              ) : (
                <BookmarkBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
              )}
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <BookmarkToggle
          addToActionItems={allBookmarksChecked}
          onToggle={handleToggleAllBookmarks}
          iconSize='medium'
          sx={{ display: 'flex', alignItems: 'center' }}
        />
      )}
    </Box>
  );

  const completeToggle = <SectionCompleteToggle isCompleted={isCompleted} onToggle={handleToggleCompleted} isSaving={isSavingCompletion} />;

  // All sections use DeclarativeLayoutRenderer with custom rendering
  return (
    <Box id={sectionId}>
      <Accordion expanded={expanded} onChange={handleExpandChange} sx={accordionSx || { px: 2, borderRadius: '10px !important' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
          {renderHeader ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {completeToggle}
                <Typography variant={titleVariant} color={titleColor} sx={{ lineHeight: 1.5, opacity: isCompleted ? 0.6 : 1 }}>
                  {title}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {renderHeader(localData)}
                {!hideActionItemBookmark && bookmarkWithChip}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {completeToggle}
                <Typography variant={titleVariant} color={titleColor} sx={{ lineHeight: 1.5, opacity: isCompleted ? 0.6 : 1 }}>
                  {title}
                </Typography>
              </Box>
              {!hideActionItemBookmark && bookmarkWithChip}
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pb: 2, position: 'relative' }}>
          <Box aria-hidden={isCompleted} {...(isCompleted ? { inert: '' } : {})} sx={isCompleted ? { opacity: 0.6 } : undefined}>
            {renderContent(localData, handleFieldChange, getField, onBook)}
          </Box>
        </AccordionDetails>
      </Accordion>
      <ActionItemsReorderModal open={reorderModalOpen} onClose={() => setReorderModalOpen(false)} />
    </Box>
  );
};
